import React, { useState, useEffect } from "react";
import ConfirmationModalNew from "../../../common/ConfirmationModalNew";
import CustomInput from "../../../common/CustomInput";
import { useDispatch, useSelector } from "react-redux";
import { setMedicationInitialData } from "../../../../redux/modules/patient/actions";
import { communicationTypeOptions, sourceOptions } from "../../create/options";
import { formatDateToDD_MMM_YYYY } from "../../../../helpers/functions";
// Revert to CustomInput dropdowns instead of CommonSelect

const AddMedicationDialog = ({
  open = false,
  onClose = () => { },
  onSave = (data) => { },
  patientId = null,
  initialData = null,
}) => {
  const dispatch = useDispatch();
  const patientsState = useSelector((state) => state.patients);
  const { medicationInitialData } = patientsState;
  const resolvedInitialData = initialData || medicationInitialData;

  const [startDate, setStartDate] = useState("");
  const [source, setSource] = useState(""); // store primitive value
  const [communicationType, setCommunicationType] = useState(""); // store primitive value
  const [prescriberName, setPrescriberName] = useState("");
  const [applyToAll, setApplyToAll] = useState(false);

  const formatToYYYYMMMDDFromHelper = (input) => {
    const formatted = formatDateToDD_MMM_YYYY(input);
    if (!formatted) return "";
    const [day, month, year] = formatted.split("-");
    if (!day || !month || !year) return formatted;
    return `${year}-${month}-${day}`;
  };

  // Set default start date to today or use prefilled data
  useEffect(() => {
    if (!open) return;

    const toDisplay = (dateInput) => {
      if (!dateInput) return "";
      const parsed = new Date(dateInput);
      return isNaN(parsed.getTime()) ? dateInput : formatToYYYYMMMDDFromHelper(parsed);
    };

    if (resolvedInitialData) {
      setStartDate(toDisplay(resolvedInitialData.startDate));
      setSource(resolvedInitialData.source || "patient");
      setCommunicationType(resolvedInitialData.communicationType || "patient_medication_list");
      setPrescriberName(resolvedInitialData.prescriberName || "");
      setApplyToAll(!!resolvedInitialData.applyToAll);
    } else {
      setStartDate(formatToYYYYMMMDDFromHelper(new Date()));
      setSource("patient");
      setCommunicationType("patient_medication_list");
      setPrescriberName("");
      setApplyToAll(false);
    }
  }, [open, resolvedInitialData]);

  const handleSave = () => {
    // Validate required fields
    if (!source || !communicationType) {
      alert("Please fill in all required fields");
      return;
    }

    const medicationData = {
      startDate,
      source: source || null,
      communicationType: communicationType || null,
      prescriberName: prescriberName.trim(),
      applyToAll
    }; 
    // Save to patient state if applyToAll is checked for prefilling
    if (applyToAll) {
      const payload = {
        startDate,
        source: source || null,
        communicationType: communicationType || null,
        prescriberName: prescriberName.trim(),
        applyToAll: true
      };
      dispatch(setMedicationInitialData(payload));
    }

    onSave(medicationData);
    handleClose();
  };

  const handleClose = () => {
    // Reset form
    setStartDate("");
    setSource("");
    setCommunicationType("");
    setPrescriberName("");
    setApplyToAll(false);
    onClose();
  };

  const isFormValid = source && communicationType;

  return (
    <ConfirmationModalNew
      open={open}
      handleClose={handleClose}
      onCross={handleClose}
      title="Add a Medication"
      titleClassName="text-teal-300 text-2xl font-semibold"
      headClassName="px-8 pt-8 pb-4"
      content={() => (
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-4">
            {/* Start Date (no calendar) */}
            <CustomInput
              type="text"
              label="Date Entered"
              placeholder="YYYY-MMM-DD"
              value={startDate}
              onChange={(e) => setStartDate(e.target ? e.target.value : e)}
              className="col-span-12 w-full text-base text-gray-800 !bg-white"
              inputClassName="!min-h-[15px]"
              showOptionalLabel={false}
              required={true}
            />

            {/* Source */}
            <CustomInput
              type="dropdown"
              label="Source"
              placeholder="Select Source"
              options={sourceOptions}
              value={source}
              onChange={(opt) => setSource(opt?.value || "")}
              showInputCancellation={true}
              onClear={() => setSource("")}
              className="col-span-12 text-gray-800 w-full !bg-white"
              showOptionalLabel={false}
              required={true}
            />

            {/* Communication Type */}
            <CustomInput
              type="dropdown"
              label="Communication Type"
              placeholder="Select Communication Type"
              options={communicationTypeOptions}
              value={communicationType}
              onChange={(opt) => setCommunicationType(opt?.value || "")}
              showInputCancellation={true}
              onClear={() => setCommunicationType("")}
              className="col-span-12 text-gray-800 w-full !bg-white"
              showOptionalLabel={false}
              required={true}
            />

            {/* Prescriber Name */}
            <CustomInput
              type="text"
              label="Prescriber Name"
              placeholder="Enter Prescriber Name"
              value={prescriberName}
              onChange={(e) => setPrescriberName(e.target.value)}
              className="col-span-12 text-gray-800 w-full !bg-white"
              inputClassName="!min-h-[15px]"
              showOptionalLabel={true}
              required={false}
            />

            {/* Checkbox */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="applyToAll"
                checked={applyToAll}
                onChange={(e) => setApplyToAll(e.target.checked)}
                className="w-2 h-2 text-teal-600 bg-white border-gray-200 rounded focus:ring-teal-500 focus:ring-2 flex-shrink-0"
              />
              <label htmlFor="applyToAll" className="text-sm font-normal text-teal-600 whitespace-nowrap">
                Select if the same information applies to <span className="font-medium">consecutive</span> medications entries.
              </label>
            </div>
          </div>
        </div>
      )}
      className="flex flex-col gap-8 max-w-lg w-[630px] bg-gray-20"
      confirmText="Save & Continue"
      confirmAction={(data) => handleSave(data)}
      disableConfirm={!isFormValid}
    />
  );
};

export default AddMedicationDialog;
